/**
 * Needs you: every decision waiting on a person, in one place, each settled
 * in one tap (shared/needsYou). Grouped by what it is about — money first,
 * because that is what an athlete notices.
 *
 * This is the Desk. It used to be a page one click away from a home screen
 * of charts; the Oct 2026 UX pass made it the home screen, because the
 * question an agent opens Reppr with is "what do I have to decide", not
 * "what is the portfolio's weighted pipeline". The same actions are what the
 * Claude connector carries out, so the app and Claude never disagree about
 * what is waiting.
 *
 * Read-only accounts see the list without the buttons; assistants, whom the
 * collection rules keep out of the agency's money, see everything else and
 * are told the money is not shown to them.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getNeedsYou, performNeedsAction } from '../api/needsYou'
import type { NeedsAction, NeedsGroup, NeedsItem } from '../shared/needsYou'
import { usePermissions } from '../hooks/usePermissions'
import { useSession } from '../hooks/useSession'
import { showToast } from './Toast'
import { safeHref } from '../utils/safeHref'
import styles from '../pages/RevenueFees.module.css'

const GROUPS: { group: NeedsGroup; blurb: string }[] = [
  { group: 'NIL Go', blurb: 'College deals with a reporting deadline or waiting to clear. Eligibility can turn on these.' },
  { group: 'Deals', blurb: 'Brands that wrote in from a partner page.' },
  { group: 'Honors', blurb: 'What the weekly sweep found. Nothing goes on a record until you say so.' },
  { group: 'Money', blurb: 'Pay that should have arrived, books that do not balance, schedules to confirm.' },
  { group: 'Contracts', blurb: 'Figures a second source disputes, and signed documents waiting on a decision.' },
  { group: 'Athletes', blurb: 'Where an athlete stands and his details, where the sources and the record disagree.' },
  { group: 'Contacts', blurb: 'Numbers and changes from your phone, waiting for a yes.' },
]

const button: React.CSSProperties = {
  background: 'none', border: '1px solid var(--line-strong)', color: 'var(--text-primary)',
  font: 'inherit', fontSize: 12, padding: '2px 9px', cursor: 'pointer', whiteSpace: 'nowrap',
}

export function NeedsRow({ item, canAct, busy, onAct }: { item: NeedsItem; canAct: boolean; busy: boolean; onAct: (a: NeedsAction) => void }) {
  return (
    <tr>
      <td style={{ width: '24%', verticalAlign: 'top' }}>
        {item.client_id
          ? <Link to={`/clients/${item.client_id}`} style={{ color: 'var(--text-primary)' }}>{item.client_name ?? 'Athlete'}</Link>
          : <span>{item.client_name ?? 'Agency'}</span>}
      </td>
      <td style={{ fontSize: 12.5, verticalAlign: 'top' }}>
        <div style={{ fontWeight: 600 }}>{item.title}</div>
        <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{item.detail}</div>
        {item.links && item.links.length > 0 && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4, fontSize: 11.5 }}>
            {item.links.map((l, i) => {
              const href = safeHref(l.url)
              return href
                ? <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{l.label}</a>
                : <span key={i}>{l.label}</span>
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          {item.actions.map((a, i) => a.type === 'open'
            ? <Link key={i} to={a.to} style={{ color: 'var(--accent)', fontSize: 12, alignSelf: 'center' }}>{a.label}</Link>
            : canAct && <button key={i} type="button" style={button} disabled={busy} onClick={() => onAct(a)}>{busy ? 'Saving…' : a.label}</button>)}
        </div>
      </td>
    </tr>
  )
}

export default function NeedsYouList() {
  const { user } = useSession()
  const { isReadOnly, isAssistant } = usePermissions()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const q = useQuery({ queryKey: ['needs-you', !isAssistant], queryFn: () => getNeedsYou(!isAssistant), enabled: !!(user as any)?.agency_id })

  async function act(item: NeedsItem, action: NeedsAction) {
    setBusy(item.id)
    try {
      const said = await performNeedsAction(action, { id: (user as any).id, agency_id: (user as any).agency_id })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['needs-you'] }),
        queryClient.invalidateQueries({ queryKey: ['money-calendar'] }),
        queryClient.invalidateQueries({ queryKey: ['balances'] }),
      ])
      if (said) showToast(`${item.client_name ? `${item.client_name}: ` : ''}${said}`, 'success')
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setBusy(null)
    }
  }

  const items = q.data?.items ?? []
  return (
    <section aria-label="Needs you">
      <p style={{ margin: '0 0 var(--space-4)', color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        {q.isLoading ? 'Checking everything waiting on you…'
          : items.length === 0 ? 'Nothing is waiting on you.'
            : `${items.length} thing${items.length === 1 ? '' : 's'} waiting on you. Each one is settled here in one tap.`}
        {isAssistant && ' Money is not shown to assistants.'}
      </p>

      {q.error ? <div className={styles.empty}>Could not load what needs you: {(q.error as Error).message}</div> : null}
      {(q.data?.errors ?? []).length > 0 && (
        <div className={styles.interpretation} role="alert">
          <strong>Not everything could be checked:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{q.data!.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {GROUPS.map(({ group, blurb }) => {
        const mine = items.filter(i => i.group === group)
        if (mine.length === 0) return null
        return (
          <div key={group} className={styles.panel} style={{ marginBottom: 'var(--space-4)' }}>
            <div className={styles.panelHead}>
              <h3 className={styles.panelTitle}>{group}</h3>
              <span className={styles.panelMeta}>{mine.length} · {blurb}</span>
            </div>
            <table className={styles.table}>
              <tbody>
                {mine.map(item => (
                  <NeedsRow key={item.id} item={item} canAct={!isReadOnly} busy={busy === item.id} onAct={a => act(item, a)} />
                ))}
              </tbody>
            </table>
          </div>
        )
      })}
    </section>
  )
}
